import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useAdjustPoints, useAdminPerson, useAdminPhotos, useResetQuestFor,
} from '../../hooks/useQuestAdmin';
import { fmtDate, periodLabel } from './questShared';
import {
  AdminPage, Avatar, BTN, BTN_SECONDARY, Card, DEPARTMENT_SOON, ErrorNote, INPUT, LABEL, Loading, Stat,
} from './adminUi';

// "Let them try again": clears their go at this Quest — attempts, the result,
// and any points it paid (taken back as a deduction, so the history shows it).
function TryAgain({ entraUserId, quest }) {
  const reset = useResetQuestFor();
  const [asking, setAsking] = useState(false);
  if (reset.isSuccess) return <span className="text-xs font-medium text-emerald-700">Reset — they can take it again</span>;
  if (!asking) {
    return (
      <button type="button" onClick={() => setAsking(true)} className={`${BTN_SECONDARY} px-3 py-1 text-xs`}>
        Let them try again
      </button>
    );
  }
  return (
    <span className="flex flex-wrap items-center justify-end gap-2 text-xs">
      <span className="text-gray-600">
        {quest.points_awarded ? `Takes back ${quest.points_awarded} points and clears` : 'Clears'} their attempts. Sure?
      </span>
      <button type="button" disabled={reset.isPending}
        onClick={() => reset.mutate({ entraUserId, questId: quest.id })}
        className="rounded-md bg-brand px-3 py-1 font-semibold text-white hover:bg-brand-dark disabled:opacity-40">
        {reset.isPending ? 'Resetting…' : 'Yes, reset'}
      </button>
      <button type="button" onClick={() => setAsking(false)} className="px-1 font-medium text-gray-500">Cancel</button>
      {reset.isError ? <ErrorNote error={reset.error} fallback="That did not reset." /> : null}
    </span>
  );
}

// One employee: their points, Quests and full points history, and the brief's
// "add points / remove points / give reason". An adjustment is a new ledger
// row, never an edit, so the history below is the audit trail.

function Adjust({ entraUserId }) {
  const adjust = useAdjustPoints();
  const [direction, setDirection] = useState('add');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [done, setDone] = useState(null);

  const save = async (e) => {
    e.preventDefault();
    const n = Math.abs(Number(amount)) * (direction === 'add' ? 1 : -1);
    try {
      await adjust.mutateAsync({ entra_user_id: entraUserId, points: n, reason });
      setDone(n);
      setAmount('');
      setReason('');
    } catch { /* shown below */ }
  };

  return (
    <form onSubmit={save} className="space-y-3 p-4">
      <div className="grid grid-cols-2 gap-1 rounded-lg bg-gray-100 p-1">
        {[['add', 'Add points'], ['remove', 'Remove points']].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setDirection(k)}
            className={`rounded-md px-3 py-1.5 text-sm font-semibold ${direction === k ? 'bg-white text-brand-dark shadow' : 'text-gray-600'}`}>
            {label}
          </button>
        ))}
      </div>
      <label className="block">
        <span className={LABEL}>Points</span>
        <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} className={INPUT} />
      </label>
      <label className="block">
        <span className={LABEL}>Reason (required — the employee sees it)</span>
        <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={255}
          placeholder={direction === 'add' ? 'e.g. Spotted the hazard at the depot' : 'e.g. Awarded twice by mistake'}
          className={INPUT} />
      </label>
      <ErrorNote error={adjust.error} />
      {done ? (
        <p className="text-sm text-emerald-700">{done > 0 ? `${done} points added.` : `${-done} points removed.`}</p>
      ) : null}
      <button type="submit" disabled={!Number(amount) || !reason.trim() || adjust.isPending} className={BTN}>
        {direction === 'add' ? 'Add points' : 'Remove points'}
      </button>
    </form>
  );
}

function QuestPerson() {
  const { oid } = useParams();
  const q = useAdminPerson(oid);
  const p = q.data;
  const photos = useAdminPhotos([oid]);

  return (
    <AdminPage title={p?.display_name || 'Employee'} back="/quest?tab=people" backLabel="People">
      <Loading q={q} what="this person" />
      {p ? (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Avatar name={p.display_name} photo={photos[oid]} size="h-16 w-16 text-lg" />
            <div className="text-sm">
              <p className="text-gray-900">{p.job_title || 'No job title recorded'}</p>
              <p className="text-gray-500">{p.email || ''}</p>
              <p className="text-gray-400" title={p.department ? undefined : DEPARTMENT_SOON}>
                Department: {p.department || 'not recorded yet'}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="This quarter" value={p.points_quarter} sub={periodLabel(p.quarter)} />
            <Stat label="Position" value={p.position ? `#${p.position}` : '—'} />
            <Stat label="All time" value={p.points_lifetime} />
            <Stat label="Quests passed" value={p.completed.filter((c) => c.passed).length} />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-1">
              <p className="border-b border-gray-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Adjust points
              </p>
              <Adjust entraUserId={p.entra_user_id} />
            </Card>

            <Card className="lg:col-span-2">
              <p className="border-b border-gray-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Points history
              </p>
              {!p.history.length ? <p className="px-4 py-6 text-center text-sm text-gray-400">No points yet.</p> : null}
              <ul className="divide-y divide-gray-100">
                {p.history.map((h) => (
                  <li key={h.id} className="flex items-start justify-between gap-3 px-4 py-2.5">
                    <span className="min-w-0">
                      <span className="block text-sm text-gray-900">{h.reason || h.label || h.activity}</span>
                      <span className="block text-xs text-gray-400">
                        {fmtDate(h.created_at)} · {periodLabel(h.quarter)}{h.awarded_by_name ? ` · by ${h.awarded_by_name}` : ''}
                      </span>
                    </span>
                    <span className={`shrink-0 text-sm font-bold tabular-nums ${h.points < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>
                      {h.points > 0 ? '+' : ''}{h.points}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          <Card>
            <p className="border-b border-gray-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Quests
            </p>
            {!p.completed.length ? <p className="px-4 py-6 text-center text-sm text-gray-400">No Quests finished yet.</p> : null}
            <ul className="divide-y divide-gray-100">
              {p.completed.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                  <span>
                    <span className="block font-medium text-gray-900">{c.title}</span>
                    <span className="block text-xs text-gray-400">
                      {fmtDate(c.completed_at)}{c.attempts ? ` · ${c.attempts} attempt${c.attempts === 1 ? '' : 's'}` : ''}
                      {c.score != null ? ` · ${c.score}%` : ''}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-3">
                    <span className={c.passed ? 'font-semibold text-emerald-700' : 'text-gray-400'}>
                      {c.passed ? `+${c.points_awarded}` : 'Not passed'}
                    </span>
                    <TryAgain entraUserId={p.entra_user_id} quest={c} />
                  </span>
                </li>
              ))}
            </ul>
          </Card>

          <Card>
            <p className="border-b border-gray-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Badges
            </p>
            <ul className="grid gap-px bg-gray-100 sm:grid-cols-2 lg:grid-cols-5">
              {(p.badges || []).map((b) => (
                <li key={b.key} className="bg-white px-4 py-3">
                  <span className={`block text-sm font-semibold ${b.earned ? 'text-amber-600' : 'text-gray-500'}`}>{b.title}</span>
                  <span className="block text-xs text-gray-400">{b.description}</span>
                  <span className="mt-1 block text-xs tabular-nums text-gray-600">
                    {b.earned ? `Earned ${fmtDate(b.earned_at)}` : `${b.current.toLocaleString('en-GB')} / ${b.target.toLocaleString('en-GB')}`}
                  </span>
                </li>
              ))}
            </ul>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <p className="border-b border-gray-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Nominations
              </p>
              {!p.nominations?.length ? <p className="px-4 py-6 text-center text-sm text-gray-400">None yet.</p> : null}
              <ul className="divide-y divide-gray-100">
                {(p.nominations || []).map((n) => (
                  <li key={n.id} className="px-4 py-2.5 text-sm">
                    <span className="block font-medium text-gray-900">
                      {n.received ? `From ${n.nominator_name || 'Unknown'}` : `For ${n.nominee_name || 'Unknown'}`} · {n.value_title}
                    </span>
                    <span className="block text-xs text-gray-400">{fmtDate(n.created_at)} · {STATUS_LABEL[n.status] || n.status}</span>
                  </li>
                ))}
              </ul>
            </Card>

            <Card>
              <p className="border-b border-gray-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Submissions
              </p>
              {!p.submissions?.length ? <p className="px-4 py-6 text-center text-sm text-gray-400">None yet.</p> : null}
              <ul className="divide-y divide-gray-100">
                {(p.submissions || []).map((s) => (
                  <li key={s.id} className="px-4 py-2.5 text-sm">
                    <span className="block font-medium text-gray-900">
                      {s.quest_title || KIND_LABEL[s.kind]}
                      {s.submitter_entra_id !== p.entra_user_id ? ` · from ${s.submitter_name || 'a colleague'}` : ''}
                      {s.subject_entra_id !== p.entra_user_id ? ` · for ${s.subject_name || 'a colleague'}` : ''}
                    </span>
                    <span className="block text-xs text-gray-400">
                      {fmtDate(s.created_at)} · {STATUS_LABEL[s.status] || s.status}
                      {s.points_awarded ? ` · +${s.points_awarded}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      ) : null}
    </AdminPage>
  );
}

const STATUS_LABEL = {
  pending: 'Waiting', reviewed: 'Reviewed', clarify: 'Asked for detail', approved: 'Approved', rejected: 'Not approved',
};
const KIND_LABEL = {
  compliment: 'Customer compliment', improvement: 'Improvement idea', colleague: 'Colleague recognition', task: 'Challenge entry',
};

export default QuestPerson;
